import React, { useRef, useState } from 'react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { 
  ShieldCheckIcon, 
  PhoneIcon,
  CheckCircleIcon,
  XMarkIcon,
  PrinterIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { toast } from 'react-toastify';

const WarrantyCertificate = ({ warrantyData, onClose }) => {
  const certificateRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  
  if (!warrantyData) return null;
  
  // 모바일 WebView 감지
  const isMobileWebView = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    // Android WebView
    if (/wv/.test(userAgent) && /Android/.test(userAgent)) return true;
    // iOS WebView
    if (/iPhone|iPad|iPod/.test(userAgent) && !window.MSStream) {
      // Safari의 경우 standalone 체크
      if (window.navigator.standalone) return true;
      // Chrome, Firefox 등의 iOS WebView
      if (!window.safari) return true;
    }
    return false;
  };
  
  const isMobile = isMobileWebView();
  
  const {
    project_title,
    construction_type,
    contractor_name,
    contractor_phone,
    customer_name,
    customer_phone,
    customer_email,
    completed_at,
    warranty_months,
    warranty_end_date,
    remaining_days,
    is_expired,
    certificate_number
  } = warrantyData;
  
  const formatDate = (date) => {
    return format(new Date(date), 'yyyy년 MM월 dd일', { locale: ko });
  };
  
  const formatPhone = (phone) => {
    if (!phone) return '';
    return phone.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
  };

  // PDF 생성 및 저장
  const generatePDF = async () => {
    try {
      setIsGenerating(true);
      
      // 인쇄 CSS 숨기기 (버튼 등)
      const printHideElements = document.querySelectorAll('.print-hide');
      printHideElements.forEach(el => el.style.display = 'none');
      
      // PDF 렌더링을 위한 스타일 적용
      const pdfStyles = document.createElement('style');
      pdfStyles.innerHTML = `
        .pdf-rendering h2 {
          color: #ffffff !important;
          opacity: 1 !important;
          visibility: visible !important;
          display: inline-block !important;
          font-weight: bold !important;
        }
        .pdf-rendering .section-title {
          background-color: transparent !important;
          color: #ffffff !important;
        }
      `;
      document.head.appendChild(pdfStyles);
      
      // PDF 렌더링 클래스 추가
      certificateRef.current.classList.add('pdf-rendering');
      
      // 모바일과 PC 구분하여 scale 조정
      const isMobileDevice = /Android|iPhone|iPad/i.test(navigator.userAgent);
      const scale = isMobileDevice ? 1.5 : 2; // 모바일은 1.5배, PC는 2배 해상도 (메모리 최적화)
      
      // HTML 요소를 캔버스로 변환 (화질 개선)
      const canvas = await html2canvas(certificateRef.current, {
        scale: scale, // 해상도 증가로 화질 개선
        logging: false,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: 800,
        windowHeight: 1200,
        imageTimeout: 15000,
        removeContainer: false,
        allowTaint: false,
        foreignObjectRendering: false,
        onclone: (clonedDoc) => {
          // 클론된 문서에서 버튼 숨기기
          const buttons = clonedDoc.querySelectorAll('.print-hide');
          buttons.forEach(button => button.style.display = 'none');
          
          // 모든 요소가 렌더링되도록 보장
          const element = clonedDoc.querySelector('#warranty-certificate');
          if (element) {
            element.style.display = 'block';
            element.style.visibility = 'visible';
            element.style.opacity = '1';
          }
        }
      });
      
      // PDF 렌더링 클래스 제거 및 스타일 정리
      certificateRef.current.classList.remove('pdf-rendering');
      document.head.removeChild(pdfStyles);
      
      // 디버깅을 위한 로그
      console.log('Canvas size:', canvas.width, 'x', canvas.height);
      console.log('Canvas data size (bytes):', canvas.toDataURL().length);
      
      // PDF 생성
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true // PDF 압축 활성화
      });
      
      // JPEG로 변환 (모바일 호환성)
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      
      // A4 사이즈에 맞게 조정
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // 실제 콘텐츠 크기를 스케일로 나눔 (픽셀 단위를 mm로 변환)
      const imgWidth = canvas.width / scale;
      const imgHeight = canvas.height / scale;
      
      // A4 비율에 맞추되 가로 여백 확보
      const margin = 10; // 10mm 여백
      const maxWidth = pdfWidth - (margin * 2);
      const maxHeight = pdfHeight - (margin * 2);
      
      // 비율 계산
      const ratio = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);
      const finalWidth = imgWidth * ratio;
      const finalHeight = imgHeight * ratio;
      
      // 중앙 정렬
      const imgX = (pdfWidth - finalWidth) / 2;
      const imgY = margin;
      
      pdf.addImage(imgData, 'JPEG', imgX, imgY, finalWidth, finalHeight);
      
      console.log('PDF generation completed');
      
      // 버튼 다시 표시
      printHideElements.forEach(el => el.style.display = '');
      
      // PDF 저장
      const fileName = `보증서_${certificate_number}_${format(new Date(), 'yyyyMMdd')}.pdf`;
      
      // 안드로이드 WebView에서 PDF 저장
      if (isMobileWebView()) {
        try {
          // 1. Android JavaScript Interface 사용 (앱에서 구현 필요)
          if (window.Android && window.Android.downloadPDF) {
            // ArrayBuffer로 변환 후 Base64 인코딩
            const pdfOutput = pdf.output('arraybuffer');
            const uint8Array = new Uint8Array(pdfOutput);
            let binaryString = '';
            for (let i = 0; i < uint8Array.length; i++) {
              binaryString += String.fromCharCode(uint8Array[i]);
            }
            const pdfBase64 = btoa(binaryString);
            
            console.log('PDF Base64 length:', pdfBase64.length);
            window.Android.downloadPDF(pdfBase64, fileName);
            toast.success('PDF가 다운로드 폴더에 저장되었습니다.');
          } 
          // 2. Blob URL을 사용한 다운로드
          else {
            const pdfBlob = pdf.output('blob');
            const blobUrl = URL.createObjectURL(pdfBlob);
            
            // 다운로드 링크 생성
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            
            // 정리
            setTimeout(() => {
              document.body.removeChild(link);
              URL.revokeObjectURL(blobUrl);
            }, 100);
            
            toast.success('PDF 다운로드가 시작되었습니다.');
          }
        } catch (error) {
          console.error('Mobile PDF save error:', error);
          alert('PDF 저장에 실패했습니다. 브라우저에서 다시 시도해주세요.');
        }
      } else {
        // PC에서는 직접 다운로드
        pdf.save(fileName);
      }
      
      setIsGenerating(false);
    } catch (error) {
      console.error('PDF 생성 오류:', error);
      setIsGenerating(false);
      
      // 메모리 부족 등의 오류 발생 시 대체 방안
      if (isMobile && error.message && error.message.includes('memory')) {
        if (window.confirm('메모리 부족으로 PDF 생성에 실패했습니다. 브라우저에서 열어보시겠습니까?')) {
          // 새 창에서 보증서 열기 (인쇄 가능)
          const printWindow = window.open('', '_blank');
          printWindow.document.write(`
            <html>
              <head>
                <title>보증서_${certificate_number}</title>
                <style>
                  @media print {
                    .no-print { display: none !important; }
                  }
                  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
                </style>
              </head>
              <body>
                ${certificateRef.current.innerHTML}
                <div class="no-print" style="text-align: center; margin-top: 20px;">
                  ${!isMobile ? '<button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; background-color: #2563eb; color: white; border: none; border-radius: 8px; margin-right: 10px;">인쇄하기</button>' : ''}
                  <button onclick="window.close()" style="padding: 10px 20px; font-size: 16px; background-color: #6b7280; color: white; border: none; border-radius: 8px;">닫기</button>
                </div>
              </body>
            </html>
          `);
          printWindow.document.close();
        }
      } else {
        alert('PDF 생성 중 오류가 발생했습니다. 다시 시도해주세요.');
      }
    }
  };
  
  const handlePrint = () => {
    // 현재 페이지에서 직접 인쇄
    window.print();
  };

  return (
    <>
      <div className="fixed inset-0 bg-white z-[100] overflow-y-auto">
        <div className="warranty-certificate w-full min-h-full relative">
          {/* 닫기 버튼 */}
          <button
            onClick={onClose}
            className="print-hide absolute top-2 right-2 sm:top-4 sm:right-4 z-10 p-2 hover:bg-gray-100 rounded-lg transition-colors bg-white shadow-md"
          >
            <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600" />
          </button>

          {/* 인쇄용 컨텐츠 래퍼 */}
          <div ref={certificateRef} id="warranty-certificate" className="warranty-certificate-content">
            {/* 보증서 헤더 */}
            <div className="warranty-header bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-6 sm:px-8 sm:py-8 relative border-2 sm:border-4 border-blue-800">
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 mb-4">
                    <div className="bg-white bg-opacity-20 p-2 sm:p-3 rounded-full">
                      <ShieldCheckIcon className="h-8 w-8 sm:h-12 sm:w-12" />
                    </div>
                    <div>
                      <h1 className="text-2xl sm:text-4xl font-bold tracking-wide">시공 품질 보증서</h1>
                      <div className="h-0.5 sm:h-1 bg-white mx-auto w-32 sm:w-48 mt-1 sm:mt-2 rounded"></div>
                      <p className="text-blue-100 text-sm sm:text-base mt-1 sm:mt-2 font-medium">Construction Quality Warranty Certificate</p>
                    </div>
                  </div>
                  <div className="certificate-number mt-4 sm:mt-6 bg-white bg-opacity-15 rounded-lg px-4 py-2 sm:px-6 sm:py-3 inline-block">
                    <span className="label text-blue-100 text-xs sm:text-sm font-medium">보증서 번호</span>
                    <span className="number font-mono font-bold text-lg sm:text-2xl tracking-wider block">{certificate_number}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 본문 내용 - 기존 UI 유지 */}
            <div className="px-4 py-4 sm:px-8 sm:py-8 space-y-6 sm:space-y-8">
              {/* 프로젝트 정보 */}
              <div className="warranty-gradient project-info bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-8 border-2 border-blue-300 shadow-lg">
                <div className="text-center mb-4 sm:mb-6">
                  <h2 className="section-title inline-block px-6 py-3 text-xl font-bold rounded-full border-2" 
                      style={{ 
                        borderColor: '#2563eb',
                        backgroundColor: '#2563eb', 
                        color: '#ffffff',
                        fontSize: '20px',
                        fontWeight: 'bold'
                      }}>
                    프로젝트 정보
                  </h2>
                </div>
                <div className="info-content bg-white rounded-lg p-4 sm:p-6 border border-blue-200">
                  <div className="info-grid grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
                    <div className="space-y-4">
                      <div className="info-item border-l-4 border-blue-500 pl-3 sm:pl-4">
                        <p className="info-label text-gray-600 text-xs sm:text-sm font-medium uppercase tracking-wide">프로젝트명</p>
                        <p className="info-value font-bold text-lg sm:text-xl text-gray-900 mt-1">{project_title}</p>
                      </div>
                      <div className="info-item border-l-4 border-indigo-500 pl-3 sm:pl-4">
                        <p className="info-label text-gray-600 text-xs sm:text-sm font-medium uppercase tracking-wide">공사 유형</p>
                        <p className="info-value font-semibold text-base sm:text-lg text-gray-900 mt-1">{construction_type}</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="info-item border-l-4 border-green-500 pl-3 sm:pl-4">
                        <p className="info-label text-gray-600 text-xs sm:text-sm font-medium uppercase tracking-wide">시공 완료일</p>
                        <p className="info-value font-semibold text-base sm:text-lg text-gray-900 mt-1">{formatDate(completed_at)}</p>
                      </div>
                      <div className="info-item border-l-4 border-purple-500 pl-3 sm:pl-4">
                        <p className="info-label text-gray-600 text-xs sm:text-sm font-medium uppercase tracking-wide">보증 기간</p>
                        <p className="info-value font-semibold text-base sm:text-lg text-gray-900 mt-1">{warranty_months}개월 ({Math.floor(warranty_months / 12)}년)</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 고객 정보 */}
              <div className="warranty-gradient customer-info bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 sm:p-8 border-2 border-purple-300 shadow-lg">
                <div className="text-center mb-4 sm:mb-6">
                  <h2 className="section-title inline-block px-6 py-3 text-xl font-bold rounded-full border-2" 
                      style={{ 
                        borderColor: '#9333ea',
                        backgroundColor: '#9333ea', 
                        color: '#ffffff',
                        fontSize: '20px',
                        fontWeight: 'bold'
                      }}>
                    고객 정보
                  </h2>
                </div>
                <div className="info-content bg-white rounded-lg p-4 sm:p-6 border border-purple-200">
                  <div className="info-grid grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
                    <div className="space-y-4">
                      <div className="info-item border-l-4 border-purple-500 pl-3 sm:pl-4">
                        <p className="info-label text-gray-600 text-xs sm:text-sm font-medium uppercase tracking-wide">고객명</p>
                        <p className="info-value font-bold text-lg sm:text-xl text-gray-900 mt-1">{customer_name || '정보 없음'}</p>
                      </div>
                      <div className="info-item border-l-4 border-pink-500 pl-3 sm:pl-4">
                        <p className="info-label text-gray-600 text-xs sm:text-sm font-medium uppercase tracking-wide">연락처</p>
                        <p className="info-value font-semibold text-base sm:text-lg text-gray-900 mt-1">{formatPhone(customer_phone) || '정보 없음'}</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="info-item border-l-4 border-indigo-500 pl-3 sm:pl-4">
                        <p className="info-label text-gray-600 text-xs sm:text-sm font-medium uppercase tracking-wide">이메일</p>
                        <p className="info-value font-semibold text-base sm:text-lg text-gray-900 mt-1">{customer_email || '정보 없음'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 보증 기간 상태 */}
              <div className="warranty-gradient warranty-info rounded-xl p-4 sm:p-8 border-2 shadow-lg" 
                   style={{ 
                     backgroundColor: is_expired ? '#f3f4f6' : '#dcfce7',
                     borderColor: is_expired ? '#9ca3af' : '#22c55e'
                   }}>
                <div className="info-content bg-white rounded-lg p-4 sm:p-6 border" 
                     style={{ 
                       backgroundColor: '#ffffff',
                       borderColor: is_expired ? '#e5e7eb' : '#bbf7d0'
                     }}>
                  <div className="text-center">
                    <h3 className="section-title text-lg sm:text-2xl font-bold mb-3" 
                        style={{ 
                          color: is_expired ? '#374151' : '#166534',
                          fontSize: '24px',
                          fontWeight: 'bold',
                          marginBottom: '12px'
                        }}>
                      보증 기간
                    </h3>
                    <p className="text-base sm:text-xl mb-2" 
                       style={{ 
                         color: '#000000',
                         fontSize: '18px',
                         fontWeight: '600',
                         marginBottom: '8px'
                       }}>
                      {formatDate(completed_at)} ~ {formatDate(warranty_end_date)}
                    </p>
                    <div className={`warranty-status ${is_expired ? 'expired' : 'valid'} inline-block px-4 py-2 rounded-lg`}
                         style={{ 
                           backgroundColor: is_expired ? '#fee2e2' : '#d1fae5',
                           color: is_expired ? '#991b1b' : '#065f46',
                           fontSize: '16px',
                           fontWeight: 'bold'
                         }}>
                      {is_expired ? '보증 기간이 만료되었습니다' : `${remaining_days}일 남음`}
                    </div>
                  </div>
                </div>
              </div>

              {/* 시공 책임자 섹션 */}
              <div className="warranty-gradient contractor-info bg-gradient-to-br from-red-50 to-pink-50 border-2 sm:border-3 border-red-300 rounded-xl p-4 sm:p-8 relative shadow-lg">
                <div className="text-center mb-4 sm:mb-6">
                  <h2 className="section-title inline-block px-6 py-3 text-xl font-bold rounded-full border-2" 
                      style={{ 
                        borderColor: '#dc2626',
                        backgroundColor: '#dc2626', 
                        color: '#ffffff',
                        fontSize: '20px',
                        fontWeight: 'bold'
                      }}>
                    시공 책임자
                  </h2>
                </div>
                
                <div className="flex items-center justify-center">
                  <div className="text-center">
                    <div className="warranty-seal inline-flex items-center justify-center w-28 h-28 sm:w-40 sm:h-40 border-3 sm:border-4 border-red-600 rounded-full bg-gradient-to-br from-red-100 to-red-200 mb-4 sm:mb-6 relative shadow-xl">
                      <div className="text-center">
                        <p className="text-red-700 font-bold text-sm sm:text-xl leading-tight">책임시공</p>
                        <p className="text-red-800 font-extrabold text-base sm:text-2xl mt-0.5 sm:mt-1">{contractor_name}</p>
                        <p className="text-red-700 font-bold text-xs sm:text-base">소장님</p>
                      </div>
                      <div className="absolute inset-2 sm:inset-3 border border-red-500 sm:border-2 rounded-full opacity-60"></div>
                      <div className="absolute inset-4 sm:inset-6 border border-red-400 rounded-full opacity-40"></div>
                    </div>
                    
                    <div className="info-content space-y-3 bg-white rounded-lg p-3 sm:p-4 border border-red-200">
                      <p className="info-value text-xl sm:text-3xl font-extrabold text-red-700">{contractor_name} 소장님</p>
                      <div className="flex items-center justify-center gap-2 sm:gap-3 bg-gray-50 rounded-lg p-2 sm:p-3">
                        <PhoneIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500" />
                        <a 
                          href={`tel:${contractor_phone}`}
                          className="info-value text-base sm:text-xl font-bold text-blue-600 hover:text-blue-700 transition-colors"
                        >
                          {formatPhone(contractor_phone)}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 보증 내용 */}
              <div className="warranty-gradient bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-4 sm:p-8 border-2 border-yellow-300 shadow-lg">
                <div className="text-center mb-4 sm:mb-6">
                  <h2 className="section-title inline-block px-6 py-3 text-xl font-bold rounded-full border-2" 
                      style={{ 
                        borderColor: '#ca8a04',
                        backgroundColor: '#ca8a04', 
                        color: '#ffffff',
                        fontSize: '20px',
                        fontWeight: 'bold'
                      }}>
                    보증 내용
                  </h2>
                </div>
                <div className="info-content bg-white rounded-lg p-4 sm:p-6 border border-yellow-200">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-base sm:text-lg text-gray-900">품질 보증</p>
                        <p className="text-gray-600 text-sm sm:text-base mt-1">시공 품질에 대한 전문가적 책임을 지겠습니다</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-base sm:text-lg text-gray-900">사후 관리</p>
                        <p className="text-gray-600 text-sm sm:text-base mt-1">보증 기간 내 문제 발생 시 성실하게 대응하겠습니다</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-base sm:text-lg text-gray-900">신속 대응</p>
                        <p className="text-gray-600 text-sm sm:text-base mt-1">연락 시 가능한 빠른 시간 내 현장 확인하겠습니다</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-gray-500 text-xs sm:text-sm">※ 일반적인 사용 범위를 벗어난 경우는 별도 상담이 필요할 수 있습니다</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 회사 정보 */}
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-4 sm:p-6 border-2 border-gray-300 shadow-lg">
                <div className="text-center">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">건기 통합관리 플랫폼</h3>
                  <p className="text-gray-600 text-sm sm:text-base">본 보증서는 건기 통합관리 플랫폼을 통해 발급되었습니다</p>
                  <p className="text-gray-500 text-xs sm:text-sm mt-2">문의: info@geongi.kr</p>
                </div>
              </div>
            </div>

            {/* 푸터 */}
            <div className="bg-gradient-to-r from-gray-100 to-gray-200 px-4 py-4 sm:px-8 sm:py-6 border-t-2 sm:border-t-4 border-gray-300">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="bg-white rounded-lg px-3 py-2 sm:px-4 sm:py-2 border border-gray-300 order-2 sm:order-1">
                  <p className="text-gray-700 font-semibold text-sm sm:text-base">발급일: {format(new Date(), 'yyyy년 MM월 dd일 HH:mm', { locale: ko })}</p>
                </div>
                <div className="flex gap-3 sm:gap-4 order-1 sm:order-2">
                  {isMobile ? (
                    <>
                      {/* 모바일: PDF 저장 */}
                      <button
                        onClick={generatePDF}
                        disabled={isGenerating}
                        className={`print-hide px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg transform hover:scale-105 text-sm flex items-center gap-2 ${
                          isGenerating ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <DocumentArrowDownIcon className="h-4 w-4" />
                        {isGenerating ? 'PDF 생성 중...' : 'PDF 저장'}
                      </button>
                    </>
                  ) : (
                    <>
                      {/* PC: PDF 저장 */}
                      <button
                        onClick={generatePDF}
                        disabled={isGenerating}
                        className={`print-hide px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold rounded-lg sm:rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg transform hover:scale-105 text-sm sm:text-base ${
                          isGenerating ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <DocumentArrowDownIcon className="h-4 w-4 sm:h-5 sm:w-5 inline-block mr-2" />
                        {isGenerating ? 'PDF 생성 중...' : 'PDF 저장'}
                      </button>
                      
                      {/* PC: 인쇄하기 - 모바일/앱에서는 숨김 */}
                      {!isMobile && (
                        <button
                          onClick={handlePrint}
                          className="print-hide px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-lg sm:rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg transform hover:scale-105 text-sm sm:text-base"
                        >
                          <PrinterIcon className="h-4 w-4 sm:h-5 sm:w-5 inline-block mr-2" />
                          인쇄하기
                        </button>
                      )}
                    </>
                  )}
                  <button
                    onClick={onClose}
                    className="print-hide px-4 py-2 sm:px-8 sm:py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-bold rounded-lg sm:rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all shadow-lg transform hover:scale-105 text-sm sm:text-base"
                  >
                    ✖️ 닫기
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WarrantyCertificate;